import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button, Card, Container } from "react-bootstrap";
import useAccess from "../../hooks/useAccess";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const DriverLicense = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: paramId } = useParams();

  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const { hasPermission } = useAccess();
  const loggedInId = localStorage.getItem("userId");
  const userId = isAdmin && paramId ? paramId : loggedInId;
  const canDeleteRecords = isAdmin && hasPermission("Task", "Task", "delete");
  const showListView =
    new URLSearchParams(location.search).get("view") === "list";

  const [records, setRecords] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        };
        const res = await axios.get(`${API_BASE}/api/driverlicense/${userId}`, {
          headers,
        });

        const grouped = new Map();
        for (const row of res.data || []) {
          if (!grouped.has(row.dl_id)) {
            grouped.set(row.dl_id, {
              dl_id: row.dl_id,
              dl_status: row.dl_status,
              dl_driver_status: row.dl_driver_status,
              dl_license_no: row.dl_license_no,
              dl_country: row.dl_country,
              dl_province: row.dl_province,
              dl_expdate: row.dl_expdate,
              latest_uploaded_at: row.uploaded_at || null,
              file_count: row.dlf_id ? 1 : 0,
            });
            continue;
          }

          const existing = grouped.get(row.dl_id);
          existing.file_count += row.dlf_id ? 1 : 0;
          if (
            row.uploaded_at &&
            (!existing.latest_uploaded_at ||
              new Date(row.uploaded_at) > new Date(existing.latest_uploaded_at))
          ) {
            existing.latest_uploaded_at = row.uploaded_at;
          }
        }

        setRecords(Array.from(grouped.values()));
      } catch (err) {
        console.error("Error fetching driver licenses", err);
      }
    };

    if (userId) fetchData();
  }, [userId]);

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-US");
  };

  const formatDriverStatus = (value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized === "active") return "Active";
    if (normalized === "inactive") return "Inactive";
    return "-";
  };

  const sortedRecords = useMemo(
    () =>
      [...records].sort((a, b) => {
        const getRank = (record) => {
          if (
            record.dl_driver_status === "active" &&
            record.dl_status === "primary"
          ) {
            return 0;
          }
          if (record.dl_driver_status === "active") return 1;
          if (record.dl_status === "primary") return 2;
          return 3;
        };

        const rankDiff = getRank(a) - getRank(b);
        if (rankDiff !== 0) return rankDiff;
        return Number(b.dl_id) - Number(a.dl_id);
      }),
    [records]
  );

  useEffect(() => {
    if (showListView || !sortedRecords.length) return;

    navigate(`/driverlicense/${userId}/detail/${sortedRecords[0].dl_id}`, {
      replace: true,
    });
  }, [navigate, showListView, sortedRecords, userId]);

  const handleDeleteRecord = async (record) => {
    if (!canDeleteRecords || !record?.dl_id) return;
    if (
      !window.confirm(
        "Delete this driver license record? This cannot be undone.",
      )
    ) {
      return;
    }

    try {
      await axios.delete(`${API_BASE}/api/driverlicense/${record.dl_id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setRecords((prev) => prev.filter((item) => item.dl_id !== record.dl_id));
    } catch (err) {
      console.error("Driver license delete failed", err);
      alert(err?.response?.data?.message || "Failed to delete record.");
    }
  };

  return (
    <Container className="form-wrapper my-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">Driver&apos;s License</h3>
        <div>
          <Button
            variant="secondary"
            className="me-2"
            onClick={() =>
              navigate(isAdmin && paramId ? `/userhome/${paramId}` : "/userhome")
            }
          >
            Return
          </Button>
          <Button
            variant="success"
            onClick={() => navigate(`/driverlicense/${userId}/new`)}
          >
            Add
          </Button>
        </div>
      </div>

      <Card>
        <Card.Body>
          {sortedRecords.length === 0 ? (
            <div className="text-muted text-center">No driver licenses found.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-bordered">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "8%" }}>#</th>
                    <th style={{ width: "18%" }}>License Type</th>
                    <th style={{ width: "18%" }}>License Number</th>
                    <th style={{ width: "14%" }}>Country</th>
                    <th style={{ width: "14%" }}>Province / State</th>
                    <th style={{ width: "12%" }}>Status</th>
                    <th style={{ width: "14%" }}>Expiry Date</th>
                    <th style={{ width: "8%" }}>Files</th>
                    <th style={{ width: "6%" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRecords.map((record, index) => (
                    <tr key={record.dl_id}>
                      <td>{index + 1}</td>
                      <td>
                        {record.dl_status === "primary"
                          ? "Primary License"
                          : "Other License"}
                      </td>
                      <td>{record.dl_license_no || "-"}</td>
                      <td>{record.dl_country || "-"}</td>
                      <td>{record.dl_province || "-"}</td>
                      <td>{formatDriverStatus(record.dl_driver_status)}</td>
                      <td>{formatDate(record.dl_expdate)}</td>
                      <td>{record.file_count || 0}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() =>
                              navigate(`/driverlicense/${userId}/detail/${record.dl_id}`)
                            }
                          >
                            Edit
                          </Button>
                          {canDeleteRecords && record.dl_status !== "primary" && (
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDeleteRecord(record)}
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default DriverLicense;
